/**
 * Metrics Service
 *
 * This service provides the "brain" for data prioritization.
 * When multiple data sources provide the same type of data,
 * this service determines which source wins based on priority order.
 *
 * Priority Rules:
 * - Priority 1 = Highest (wins all conflicts)
 * - Priority 6 = Lowest
 * - Only connected sources are considered
 * - If highest-priority source has no data, falls back to next priority
 */

import { financialData, TODAY } from "../data/financial-systems";
import { DataSourceConfig } from "../types/dataSource";

/**
 * Data source type mappings
 * Defines which source types can provide which metrics
 */
export const CASH_SOURCES = [
  "Accounting System",
  "Access Financials",
  "Plaid",
  "Database",
];
const REVENUE_SOURCES = [
  "Accounting System",
  "Access Financials",
  "CRM System",
  "Database",
];
const EXPENSE_SOURCES = [
  "Accounting System",
  "Access Financials",
  "Payroll System",
  "Paycircle",
  "Database",
];
const BUDGET_SOURCES = ["Excel Spreadsheet", "Database"];

/**
 * Maps a data source to its underlying dataset
 * Returns the appropriate data based on source ID
 */
const getDataForSource = (source: DataSourceConfig) => {
  switch (source.id) {
    case "demo-accounting-001":
      return financialData;
    case "demo-payroll-001":
      return financialData;
    case "demo-crm-001":
      return financialData;
    case "demo-finops-001":
      return financialData;
    case "demo-po-001":
      return financialData;
    case "demo-budget-001":
      return financialData;
    default:
      console.warn(`[Metrics Service] Unknown source ID: ${source.id}`);
      return null;
  }
};

/**
 * Core priority resolution algorithm
 *
 * @param dataSources - All configured data sources
 * @param sourceTypes - Types of sources that can provide this data
 * @param dataExtractor - Function to extract data from a source
 * @param metricName - Name of metric (for debugging)
 * @returns Data from highest-priority source, or null if not found
 */
const getDataFromPrioritizedSource = <T>(
  dataSources: DataSourceConfig[],
  sourceTypes: string[],
  dataExtractor: (source: DataSourceConfig, data: any) => T | null,
  metricName: string,
): T | null => {
  // 1. Filter to relevant source types that are connected
  const relevantSources = dataSources.filter(
    (s) => sourceTypes.includes(s.type) && s.status === "connected",
  );

  if (relevantSources.length === 0) {
    console.warn(
      `[Metrics Service] No connected sources found for ${metricName}`,
    );
    return null;
  }

  // 2. Sort by priority (1 = highest)
  const sortedSources = [...relevantSources].sort(
    (a, b) => a.priority - b.priority,
  );

  // 3. Try each source in priority order
  for (const source of sortedSources) {
    const sourceData = getDataForSource(source);
    if (!sourceData) {
      console.warn(
        `[Metrics Service] No data available for source: ${source.name}`,
      );
      continue;
    }

    const extractedData = dataExtractor(source, sourceData);
    if (extractedData !== null && extractedData !== undefined) {
      return extractedData;
    }
  }

  // 4. No data found in any source
  console.warn(
    `[Metrics Service] ⚠️ ${metricName} - No data found in any source`,
  );
  return null;
};

/**
 * Get total cash position across all bank accounts
 * Respects data source priority
 *
 * @param dataSources - All configured data sources
 * @returns Total cash balance, or 0 if not found
 */
export const getCashPosition = (dataSources: DataSourceConfig[]): number => {
  const result = getDataFromPrioritizedSource(
    dataSources,
    CASH_SOURCES,
    (_source, data) => {
      if (data.bankAccounts && Array.isArray(data.bankAccounts)) {
        const total = data.bankAccounts.reduce(
          (sum: number, account: any) =>
            sum + (account.balance || account.currentBalance || 0),
          0,
        );
        return total;
      }
      return null;
    },
    "Cash Position",
  );

  return result ?? 0;
};

/**
 * Get the last successful sync timestamp of the cash data source
 * Uses the highest-priority connected cash source, falling back to the most
 * recent sync across connected cash sources.
 *
 * @param dataSources - All configured data sources
 * @returns ISO timestamp of the last sync, or null if never synced
 */
export const getCashSourceLastSync = (
  dataSources: DataSourceConfig[],
): string | null => {
  const cashSources = dataSources
    .filter((s) => CASH_SOURCES.includes(s.type) && s.status === "connected")
    .sort((a, b) => a.priority - b.priority);

  const prioritised = cashSources.find((s) => s.lastSync);
  if (prioritised?.lastSync) return prioritised.lastSync;

  return mostRecentSync(cashSources.map((s) => s.lastSync));
};

/**
 * Pick the most recent timestamp from a list of ISO timestamps
 */
export const mostRecentSync = (
  timestamps: (string | null | undefined)[],
): string | null => {
  return timestamps.reduce<string | null>((latest, current) => {
    if (!current) return latest;
    if (!latest) return current;
    return new Date(current).getTime() > new Date(latest).getTime()
      ? current
      : latest;
  }, null);
};

/**
 * Get revenue forecast for a given time period
 * Respects data source priority
 *
 * @param dataSources - All configured data sources
 * @param startDate - Start of forecast period
 * @param endDate - End of forecast period
 * @returns Projected revenue, or 0 if not found
 */
export const getRevenueForecast = (
  dataSources: DataSourceConfig[],
  startDate: Date,
  endDate: Date,
): number => {
  const result = getDataFromPrioritizedSource(
    dataSources,
    REVENUE_SOURCES,
    (_source, data) => {
      let total = 0;

      // Add invoice revenue
      if (data.invoices && Array.isArray(data.invoices)) {
        const filteredInvoices = data.invoices.filter((inv: any) => {
          // Skip paid invoices
          if (inv.status === "paid" || inv.status === "Paid") {
            return false;
          }

          // Use dueDate for filtering (when payment is expected)
          if (!inv.dueDate) {
            return false;
          }

          // Handle both Date objects and strings
          const dueDate =
            inv.dueDate instanceof Date ? inv.dueDate : new Date(inv.dueDate);

          // Check if date is valid
          if (isNaN(dueDate.getTime())) {
            return false;
          }

          const inRange = dueDate >= startDate && dueDate <= endDate;

          return inRange;
        });

        const invoiceRevenue = filteredInvoices.reduce(
          (sum: number, inv: any) => sum + (inv.amount || inv.totalAmount || 0),
          0,
        );
        total += invoiceRevenue;
      }

      // Add CRM opportunity revenue (weighted by probability)
      if (
        data.crmData?.opportunities &&
        Array.isArray(data.crmData.opportunities)
      ) {
        const opportunityRevenue = data.crmData.opportunities
          .filter((opp: any) => {
            const closeDate = new Date(opp.expectedCloseDate);
            return closeDate >= startDate && closeDate <= endDate;
          })
          .reduce(
            (sum: number, opp: any) =>
              sum + (opp.value || 0) * ((opp.probability || 0) / 100),
            0,
          );
        total += opportunityRevenue;
      }

      return total > 0 ? total : null;
    },
    "Revenue Forecast",
  );

  return result ?? 0;
};

/**
 * Get expense forecast for a given time period
 * Respects data source priority
 *
 * @param dataSources - All configured data sources
 * @param startDate - Start of forecast period
 * @param endDate - End of forecast period
 * @returns Projected expenses, or 0 if not found
 */
export const getExpenseForecast = (
  dataSources: DataSourceConfig[],
  startDate: Date,
  endDate: Date,
): number => {
  const result = getDataFromPrioritizedSource(
    dataSources,
    EXPENSE_SOURCES,
    (_source, data) => {
      let total = 0;

      // Add bill expenses
      if (data.bills && Array.isArray(data.bills)) {
        const billExpenses = data.bills
          .filter((bill: any) => {
            // Skip paid bills
            if (bill.status === "paid" || bill.status === "Paid") {
              return false;
            }
            // Use dueDate for filtering (when payment is due)
            if (!bill.dueDate) {
              return false;
            }
            const dueDate =
              bill.dueDate instanceof Date
                ? bill.dueDate
                : new Date(bill.dueDate);
            return dueDate >= startDate && dueDate <= endDate;
          })
          .reduce(
            (sum: number, bill: any) =>
              sum + (bill.amount || bill.totalAmount || 0),
            0,
          );
        total += billExpenses;
      }

      // Add payroll expenses
      if (data.payroll && Array.isArray(data.payroll)) {
        const payrollExpenses = data.payroll
          .filter((entry: any) => {
            const payDate = new Date(entry.payDate);
            return payDate >= startDate && payDate <= endDate;
          })
          .reduce((sum: number, entry: any) => sum + (entry.grossPay || 0), 0);
        total += payrollExpenses;
      }

      return total > 0 ? total : null;
    },
    "Expense Forecast",
  );

  return result ?? 0;
};

/**
 * Get budget data for a given time period
 * Respects data source priority
 *
 * @param dataSources - All configured data sources
 * @param startDate - Start of budget period
 * @param endDate - End of budget period
 * @returns Budget data, or null if not found
 */
export const getBudgetData = (
  dataSources: DataSourceConfig[],
  startDate: Date,
  endDate: Date,
): { revenue: number; expenses: number } | null => {
  const result = getDataFromPrioritizedSource(
    dataSources,
    BUDGET_SOURCES,
    (_source, data) => {
      if (
        data.budgetData?.monthlyBudgets &&
        Array.isArray(data.budgetData.monthlyBudgets)
      ) {
        const budgets = data.budgetData.monthlyBudgets.filter((budget: any) => {
          const budgetDate = new Date(budget.month);
          return budgetDate >= startDate && budgetDate <= endDate;
        });

        if (budgets.length === 0) return null;

        const revenue = budgets.reduce(
          (sum: number, b: any) => sum + (b.revenue || 0),
          0,
        );
        const expenses = budgets.reduce(
          (sum: number, b: any) => sum + (b.expenses || 0),
          0,
        );

        return { revenue, expenses };
      }
      return null;
    },
    "Budget Data",
  );

  return result;
};

/**
 * Get projected cash position for a future date
 * Calculates: current cash + expected revenue - expected expenses
 *
 * @param dataSources - All configured data sources
 * @param daysAhead - Number of days into the future
 * @returns Projected cash position
 */
export const getProjectedCash = (
  dataSources: DataSourceConfig[],
  daysAhead: number,
): number => {
  const currentCash = getCashPosition(dataSources);
  const now = TODAY;
  const futureDate = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000);

  const expectedRevenue = getRevenueForecast(dataSources, now, futureDate);
  const expectedExpenses = getExpenseForecast(dataSources, now, futureDate);

  const projected = currentCash + expectedRevenue - expectedExpenses;

  return projected;
};

/**
 * Calculate runway/coverage (months of cash remaining)
 * Based on current cash and average monthly expenses (burn rate)
 *
 * Formula: Runway (months) = Current Cash ÷ Average Monthly Expenses
 *
 * Note: This metric can be displayed as "Runway" or "Coverage" depending on
 * user terminology preferences. The calculation is the same.
 *
 * Uses historical paid bills + payroll from past 90 days to calculate average monthly burn
 *
 * @param dataSources - All configured data sources
 * @returns Runway in months
 */
export const calculateRunway = (dataSources: DataSourceConfig[]): number => {
  const currentCash = getCashPosition(dataSources);
  const now = TODAY;

  // Look back 90 days to calculate average monthly expenses
  // Section 2 data has paid bills in the last 90 days (0-90 days ago)
  const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

  // Get historical expenses from paid bills and payroll
  const result = getDataFromPrioritizedSource(
    dataSources,
    EXPENSE_SOURCES,
    (_source, data) => {
      let totalExpenses = 0;
      let billCount = 0;
      let payrollCount = 0;

      // Add PAID bill expenses (actual historical spending)
      if (data.bills && Array.isArray(data.bills)) {
        const paidBillsInRange = data.bills.filter((bill: any) => {
          // Only include PAID bills
          if (bill.status !== "paid" && bill.status !== "Paid") {
            return false;
          }
          // Use paidDate for historical analysis (when the bill was actually paid)
          if (!bill.paidDate) {
            return false;
          }
          const paidDate =
            bill.paidDate instanceof Date
              ? bill.paidDate
              : new Date(bill.paidDate);
          // Look at bills paid in the last 90 days
          return paidDate >= ninetyDaysAgo && paidDate <= now;
        });

        const paidBillExpenses = paidBillsInRange.reduce(
          (sum: number, bill: any) =>
            sum + (bill.amount || bill.totalAmount || 0),
          0,
        );

        billCount = paidBillsInRange.length;
        totalExpenses += paidBillExpenses;
      }

      // Add payroll expenses (always actual/paid)
      if (data.payrollRuns && Array.isArray(data.payrollRuns)) {
        const paidPayrollInRange = data.payrollRuns.filter((run: any) => {
          // Only include PAID payroll runs
          if (run.status !== "paid") {
            return false;
          }
          const runDate = new Date(run.runDate);
          // Look at payroll paid in the last 90 days
          return runDate >= ninetyDaysAgo && runDate <= now;
        });

        const payrollExpenses = paidPayrollInRange.reduce(
          (sum: number, run: any) => sum + (run.totalGross || 0),
          0,
        );

        payrollCount = paidPayrollInRange.length;
        totalExpenses += payrollExpenses;
      }

      return totalExpenses > 0 ? totalExpenses : null;
    },
    "Historical Expenses",
  );

  const totalExpensesLast90Days = result ?? 0;

  // Calculate average monthly expenses (90 days = 3 months)
  const averageMonthlyExpenses = totalExpensesLast90Days / 3;

  if (averageMonthlyExpenses <= 0) {
    return 0;
  }

  const runway = currentCash / averageMonthlyExpenses;

  return Math.round(runway * 10) / 10; // Round to 1 decimal
};

/**
 * Get count of overdue invoices
 *
 * @param dataSources - All configured data sources
 * @returns Number of overdue invoices
 */
export const getOverdueInvoiceCount = (
  dataSources: DataSourceConfig[],
): number => {
  const result = getDataFromPrioritizedSource(
    dataSources,
    REVENUE_SOURCES,
    (_source, data) => {
      if (data.invoices && Array.isArray(data.invoices)) {
        const overdueCount = data.invoices.filter(
          (inv: any) => inv.status === "overdue",
        ).length;
        return overdueCount;
      }
      return null;
    },
    "Overdue Invoice Count",
  );

  return result || 0;
};

/**
 * Get total amount of overdue invoices
 *
 * @param dataSources - All configured data sources
 * @returns Total amount of overdue invoices
 */
export const getOverdueInvoiceAmount = (
  dataSources: DataSourceConfig[],
): number => {
  const result = getDataFromPrioritizedSource(
    dataSources,
    REVENUE_SOURCES,
    (_source, data) => {
      if (data.invoices && Array.isArray(data.invoices)) {
        const overdueAmount = data.invoices
          .filter((inv: any) => inv.status === "overdue")
          .reduce((sum: number, inv: any) => sum + (inv.amount || 0), 0);
        return overdueAmount;
      }
      return null;
    },
    "Overdue Invoice Amount",
  );

  return result || 0;
};

/**
 * Get count of upcoming payments (bills due in next 90 days)
 *
 * @param dataSources - All configured data sources
 * @returns Number of upcoming payments
 */
export const getUpcomingPaymentCount = (
  dataSources: DataSourceConfig[],
): number => {
  const result = getDataFromPrioritizedSource(
    dataSources,
    EXPENSE_SOURCES,
    (_source, data) => {
      if (data.bills && Array.isArray(data.bills)) {
        const now = TODAY;
        const thirtyDaysFromNow = new Date(
          now.getTime() + 30 * 24 * 60 * 60 * 1000,
        );
        const upcomingCount = data.bills.filter((bill: any) => {
          if (bill.status === "paid" || bill.status === "Paid") return false;
          if (!bill.dueDate) return false;
          const dueDate = new Date(bill.dueDate);
          return dueDate >= now && dueDate <= thirtyDaysFromNow;
        }).length;
        return upcomingCount;
      }
      return null;
    },
    "Upcoming Payment Count",
  );

  return result || 0;
};

/**
 * Get total amount of upcoming payments (bills due in next 90 days)
 *
 * @param dataSources - All configured data sources
 * @returns Total amount of upcoming payments
 */
export const getUpcomingPaymentAmount = (
  dataSources: DataSourceConfig[],
): number => {
  const result = getDataFromPrioritizedSource(
    dataSources,
    EXPENSE_SOURCES,
    (_source, data) => {
      if (data.bills && Array.isArray(data.bills)) {
        const now = TODAY;
        const thirtyDaysFromNow = new Date(
          now.getTime() + 30 * 24 * 60 * 60 * 1000,
        );
        const upcomingAmount = data.bills
          .filter((bill: any) => {
            if (bill.status === "paid" || bill.status === "Paid") return false;
            if (!bill.dueDate) return false;
            const dueDate = new Date(bill.dueDate);
            return dueDate >= now && dueDate <= thirtyDaysFromNow;
          })
          .reduce(
            (sum: number, bill: any) =>
              sum + (bill.amount || bill.totalAmount || 0),
            0,
          );
        return upcomingAmount;
      }
      return null;
    },
    "Upcoming Payment Amount",
  );

  return result || 0;
};

/**
 * Dashboard Metrics Interface
 * Matches the structure expected by dashboard components
 */
export interface DashboardMetrics {
  currentCash: number;
  projectedCash30Days: number;
  projectedCash90Days: number;
  runway: number;
  overdueInvoices: number;
  overdueAmount: number;
  upcomingPayments: number;
  upcomingAmount: number;
  variance?: number;
  varianceAmount?: number;
  // Revenue/Expense forecasts for drill-downs
  expected30DayIncome: number;
  expected30DayExpenses: number;
  expected90DayIncome: number;
  expected90DayExpenses: number;
  // Variance card — forecast vs actual for weekly and monthly periods
  weeklyForecast: number;
  weeklyActual: number;
  monthlyForecast: number;
  monthlyActual: number;
  // Daily burn rate for runway drill-down (monthly expenses ÷ 30)
  dailyBurn: number;
  // Cash availability composition
  // availableCash  = currentCash − totalRestricted
  // totalFacilityHeadroom = sum of (facilityLimit − facilityDrawn) across all accounts
  // maxAvailableCash = availableCash + totalFacilityHeadroom
  totalRestricted: number;
  availableCash: number;
  totalFacilityLimit: number;
  totalFacilityDrawn: number;
  totalFacilityHeadroom: number;
  maxAvailableCash: number;
}

/**
 * Get all dashboard metrics at once
 * Convenience function that returns complete dashboard data
 *
 * @param dataSources - All configured data sources
 * @returns Complete dashboard metrics object
 */
export const getDashboardMetrics = (
  dataSources: DataSourceConfig[],
): DashboardMetrics => {
  // Calculate date ranges for forecasts
  const now = TODAY;
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const oneMonthFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const threeMonthsFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

  // Derive variance actuals from completed bank transactions in the past 7 / 30 days
  const completedTxns = financialData.bankTransactions.filter(
    (t: { date: Date; status: string }) => t.status === "completed",
  );
  const weeklyActualInflow = completedTxns
    .filter(
      (t: { date: Date; amount: number }) =>
        t.date >= sevenDaysAgo && t.date <= now && t.amount > 0,
    )
    .reduce((sum: number, t: { amount: number }) => sum + t.amount, 0);
  const weeklyActualOutflow = completedTxns
    .filter(
      (t: { date: Date; amount: number }) =>
        t.date >= sevenDaysAgo && t.date <= now && t.amount < 0,
    )
    .reduce(
      (sum: number, t: { amount: number }) => sum + Math.abs(t.amount),
      0,
    );
  const monthlyActualInflow = completedTxns
    .filter(
      (t: { date: Date; amount: number }) =>
        t.date >= thirtyDaysAgo && t.date <= now && t.amount > 0,
    )
    .reduce((sum: number, t: { amount: number }) => sum + t.amount, 0);
  const monthlyActualOutflow = completedTxns
    .filter(
      (t: { date: Date; amount: number }) =>
        t.date >= thirtyDaysAgo && t.date <= now && t.amount < 0,
    )
    .reduce(
      (sum: number, t: { amount: number }) => sum + Math.abs(t.amount),
      0,
    );

  // Net cash movement (inflows - outflows) for the period
  const weeklyActual = weeklyActualInflow - weeklyActualOutflow;
  const monthlyActual = monthlyActualInflow - monthlyActualOutflow;

  // Derive variance forecasts from the same period windows using revenue/expense forecasts
  const weeklyForecastIncome = getRevenueForecast(
    dataSources,
    sevenDaysAgo,
    now,
  );
  const weeklyForecastExpenses = getExpenseForecast(
    dataSources,
    sevenDaysAgo,
    now,
  );
  const monthlyForecastIncome = getRevenueForecast(
    dataSources,
    thirtyDaysAgo,
    now,
  );
  const monthlyForecastExpenses = getExpenseForecast(
    dataSources,
    thirtyDaysAgo,
    now,
  );
  const weeklyForecast = weeklyForecastIncome - weeklyForecastExpenses;
  const monthlyForecast = monthlyForecastIncome - monthlyForecastExpenses;

  // Daily burn: monthly operating expenses ÷ 30 — used for runway drill-down
  // Derived from the same expense calculation used in calculateRunway()
  const thirtyDayExpenses = getExpenseForecast(dataSources, thirtyDaysAgo, now);
  const dailyBurn =
    thirtyDayExpenses > 0 ? Math.round(thirtyDayExpenses / 30) : 2112;
  // Fallback: £63,352 monthly expenses (sum of MONTHLY_EXPENSES constants) ÷ 30 = £2,112/day

  // Cash availability composition — derived from bank account facility and restriction data
  // totalRestricted     = sum of all restrictedFund amounts across all accounts
  // availableCash       = ledger balance − restricted (cash BuildRight can actually spend today)
  // totalFacilityLimit  = sum of all credit facility limits
  // totalFacilityDrawn  = sum of all drawn amounts
  // totalFacilityHroom  = undrawn headroom (available to draw if needed)
  // maxAvailableCash    = availableCash + facilityHeadroom (absolute maximum available)
  const currentCash = getCashPosition(dataSources);
  const totalRestricted = financialData.bankAccounts.reduce(
    (sum: number, acc: any) => {
      if (!acc.restrictedFunds) return sum;
      return (
        sum +
        acc.restrictedFunds.reduce(
          (s: number, r: any) => s + (r.restrictedAmount || 0),
          0,
        )
      );
    },
    0,
  );
  const availableCash = currentCash - totalRestricted;
  const totalFacilityLimit = financialData.bankAccounts.reduce(
    (sum: number, acc: any) =>
      sum +
      (acc.creditFacilities ?? []).reduce(
        (s: number, f: any) => s + (f.facilityLimit || 0),
        0,
      ),
    0,
  );
  const totalFacilityDrawn = financialData.bankAccounts.reduce(
    (sum: number, acc: any) =>
      sum +
      (acc.creditFacilities ?? []).reduce(
        (s: number, f: any) => s + (f.facilityDrawn || 0),
        0,
      ),
    0,
  );
  const totalFacilityHeadroom = totalFacilityLimit - totalFacilityDrawn;
  const maxAvailableCash = availableCash + totalFacilityHeadroom;

  const metrics = {
    currentCash,
    projectedCash30Days: getProjectedCash(dataSources, 30),
    projectedCash90Days: getProjectedCash(dataSources, 90),
    runway: calculateRunway(dataSources),
    overdueInvoices: getOverdueInvoiceCount(dataSources),
    overdueAmount: getOverdueInvoiceAmount(dataSources),
    upcomingPayments: getUpcomingPaymentCount(dataSources),
    upcomingAmount: getUpcomingPaymentAmount(dataSources),
    // Calculate revenue/expense forecasts ONCE for drill-downs
    expected30DayIncome: getRevenueForecast(dataSources, now, oneMonthFromNow),
    expected30DayExpenses: getExpenseForecast(
      dataSources,
      now,
      oneMonthFromNow,
    ),
    expected90DayIncome: getRevenueForecast(
      dataSources,
      now,
      threeMonthsFromNow,
    ),
    expected90DayExpenses: getExpenseForecast(
      dataSources,
      now,
      threeMonthsFromNow,
    ),
    // Variance card — forecast vs actual net cash movement
    weeklyForecast,
    weeklyActual,
    monthlyForecast,
    monthlyActual,
    // Daily burn for runway drill-down
    dailyBurn,
    // Cash availability composition
    totalRestricted,
    availableCash,
    totalFacilityLimit,
    totalFacilityDrawn,
    totalFacilityHeadroom,
    maxAvailableCash,
  };

  return metrics;
};

/**
 * Get all metrics for a given time period
 * Convenience function that calls all metric functions
 *
 * @param dataSources - All configured data sources
 * @param startDate - Start of period
 * @param endDate - End of period
 * @returns Object containing all metrics
 */
export const getAllMetrics = (
  dataSources: DataSourceConfig[],
  startDate: Date,
  endDate: Date,
) => {
  return {
    cashPosition: getCashPosition(dataSources),
    revenueForecast: getRevenueForecast(dataSources, startDate, endDate),
    expenseForecast: getExpenseForecast(dataSources, startDate, endDate),
    budgetData: getBudgetData(dataSources, startDate, endDate),
  };
};
